import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CalibrationScopeItem } from "@/api/calibration";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const COMPLEXITIES = ["low", "medium", "high"] as const;

export interface ScopeItemPickerProps {
  items: CalibrationScopeItem[];
  /** A aprovação de onde a lista saiu, para o texto dizer qual é. */
  approvalVersion?: number | null;
  /** O que a publicação não tinha: entra com a banda do catálogo, para revisar. */
  entered?: Set<string>;
  /** Dias de referência por banda, com o ajuste de parâmetro da tela: é por eles
   *  que conta o item sem curadoria. */
  referenceDays?: Record<string, number>;
  reclassified: Record<string, string>;
  onReclassify: (code: string, complexity: string) => void;
}

/**
 * A lista da Calibragem, com busca e filtro por LoB (22/10/2026).
 *
 * **A lista é o Fit-to-Standard.** São os itens Ativar da aprovação vigente
 * que o catálogo de esforço conhece, e a tela não escolhe item: antes ela
 * mostrava o catálogo inteiro com caixas de seleção, e a Adeste publicou 304
 * itens com 130 Ativar no escopo. Item que falta se ativa no Functional, e
 * entra aqui pela aprovação seguinte.
 *
 * **Reclassificar achata.** Trocar a banda de um item substitui os dias dele
 * pelo valor de referência da banda, o que achata a variação que a curadoria
 * registrou. É deliberado: a pessoa está decidindo o tratamento. O motor faz
 * isso, e a tela avisa com o selo.
 *
 * **Item sem curadoria.** O Ativar que o catálogo de esforço não tem entra na
 * lista marcado, contando pelos dias de referência da banda, que nasce média.
 * Não há o que achatar: a banda é tudo o que ele tem. Foi o caso do 2RP e do 6VB
 * na AL5 PS4 até a curadoria do Marco entrar no catálogo (22/10/2026).
 */
export function ScopeItemPicker({
  items,
  approvalVersion,
  entered,
  referenceDays,
  reclassified,
  onReclassify,
}: ScopeItemPickerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [lob, setLob] = useState("all");

  const lobs = useMemo(
    () => Array.from(new Set(items.map((i) => i.lob).filter(Boolean))).sort(),
    [items],
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return items.filter((item) => {
      if (lob !== "all" && item.lob !== lob) return false;
      if (!needle) return true;
      return (
        item.code.toLowerCase().includes(needle) ||
        (item.name ?? "").toLowerCase().includes(needle)
      );
    });
  }, [items, query, lob]);

  return (
    <div className="flex flex-col gap-3">
      {approvalVersion != null ? (
        <p className="text-xs text-muted-foreground" data-testid="calibration-list-origin">
          {t(
            "The list is the items to activate in the approved scope (v{{version}}). To add or remove an item, change its verdict in the Functional and approve the scope again.",
            { version: approvalVersion },
          )}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Search by code or name")}
          aria-label={t("Search scope items")}
          className="max-w-xs"
        />
        <Select value={lob} onValueChange={setLob}>
          <SelectTrigger className="w-48" aria-label={t("Filter by line of business")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("All lines of business")}</SelectItem>
            {lobs.map((value) => (
              <SelectItem key={value} value={value}>
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="self-center text-xs text-muted-foreground">
          {t("{{shown}} of {{total}} items to activate", {
            shown: visible.length,
            total: items.length,
          })}
        </span>
      </div>

      {/* O cabeçalho existe por causa do selector: sozinho, "Baixa/Média/Alta"
          não diz de que grandeza está falando. As larguras aqui repetem as das
          linhas para as colunas ficarem alinhadas. */}
      <div className="flex items-center gap-3 border-b px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span className="w-12 shrink-0">{t("Code")}</span>
        <span className="min-w-0 flex-1">{t("Scope item")}</span>
        <span className="w-14 shrink-0 text-right">{t("Base days")}</span>
        <span className="w-28 shrink-0">{t("Complexity")}</span>
      </div>

      <ul className="max-h-[calc(100vh-24rem)] min-h-[32rem] divide-y overflow-y-auto">
        {visible.map((item) => {
          const band = reclassified[item.code] ?? item.complexity;
          const curated = item.curated !== false;
          const moved = curated && band !== item.complexity;
          const days = curated ? item.base_days : referenceDays?.[band];
          return (
            <li
              key={item.code}
              data-testid={`calibration-item-${item.code}`}
              className="flex items-center gap-3 px-3 py-2"
            >
              <code className="w-12 shrink-0 text-xs text-muted-foreground">{item.code}</code>
              <span className="min-w-0 flex-1 truncate text-sm">{item.name ?? item.code}</span>
              {!curated ? (
                <Badge
                  variant="outline"
                  className="shrink-0 border-sky-300 text-[10px] text-sky-800"
                  title={t(
                    "Not in the effort catalogue: counted with the reference days of the chosen complexity.",
                  )}
                >
                  {t("not curated")}
                </Badge>
              ) : null}
              {entered?.has(item.code) ? (
                <Badge variant="outline" className="shrink-0 border-amber-300 text-[10px] text-amber-800">
                  {t("new in scope")}
                </Badge>
              ) : null}
              <span
                className={`w-14 shrink-0 text-right text-xs text-muted-foreground${curated ? "" : " italic"}`}
              >
                {days != null ? `${days}d` : "—"}
              </span>
              <Select
                value={band}
                onValueChange={(value) => onReclassify(item.code, value)}
              >
                <SelectTrigger
                  className="w-28 shrink-0"
                  aria-label={t("Complexity of {{code}}", { code: item.code })}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPLEXITIES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {moved ? (
                <Badge variant="outline" className="shrink-0 text-[10px]">
                  {t("flattened")}
                </Badge>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
